import type { BootError } from '#~/depot/types.js';
import { Sel, STagNew } from '#~/stag/index.js';
import { useRef, useState, type ReactElement } from 'react';
import { getBootSnapshot, recoverBoot } from '../root/boot-budget.js';

/**
 * Which code path put this screen on the user's display. Five call sites render
 * the SAME markup, so neither the DOM nor a CI log could say which one fired --
 * a launch-budget expiry and a thrown render error were indistinguishable after
 * the fact, and reading it wrong costs a day. Emitted as `data-boot-error-source`
 * next to the existing stag (additive: no selector changes meaning).
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
export type BootErrorSource =
  | 'budget-gate'
  | 'error-boundary'
  | 'boot-overlay'
  | 'auto-link'
  | 'auth-guard';

interface BootErrorScreenProps {
  readonly error: BootError;
  readonly selector?: Sel;
  readonly source: BootErrorSource;
}

export function BootErrorScreen({
  error,
  selector = Sel.BootOverlay,
  source
}: BootErrorScreenProps): ReactElement {
  const [pending, setPending] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const callerRetryFired = useRef(false);
  const retry = (): void => {
    if (pending) return;
    // The caller's retry wins whenever it supplies one, initialized budget or
    // not. AutoLinkFailureState passes handleAutoLinkRetry, which clears THIS
    // user's exhausted auto-link attempts before reloading; generic boot
    // recovery skips that reset, so an exhausted failure just loops back to the
    // same screen. A boot-specific caller that wants the shared owner calls it
    // from its own retryFn.
    if (error.retryFn) {
      // Guarded by a ref, not `pending`: the caller's retry ends in a reload, so
      // re-rendering a disabled button buys nothing and would churn state on a
      // document that is going away. The ref still swallows a double click.
      if (callerRetryFired.current) return;
      callerRetryFired.current = true;
      error.retryFn();
      return;
    }
    if (!getBootSnapshot()) {
      window.location.reload();
      return;
    }
    setPending(true);
    void recoverBoot('retry').then((result) => {
      if (!result.accepted) {
        setPending(false);
        setRecoveryError(
          result.reason === 'shutting-down'
            ? 'Studio is shutting down.'
            : 'Studio could not retry. Please try again.'
        );
      }
    });
  };
  return (
    // Stable stag on the boot-error container so the HIL popup-watch can address
    // the whole error screen, not just the retry button's data-testid.
    // Per rule: playwright-stag-selectors-only (ai.knowledge/global).
    <div
      className='flex h-screen w-full flex-col items-center justify-center bg-dark'
      data-boot-error-source={source}
      {...STagNew(Sel.BootOverlay, 'error')}
    >
      <div className='flex flex-col items-center gap-16 text-center'>
        <div className='flex flex-col items-center gap-8'>
          <svg
            className='text-error'
            width='48'
            height='48'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <circle cx='12' cy='12' r='10' />
            <path d='M12 8v4' />
            <circle cx='12' cy='16' r='0.5' fill='currentColor' stroke='none' />
          </svg>
          <h1 className='font-semibold text-xl text-white'>{error.message}</h1>
          {/* ENG-4166 D3 authored guidance (e.g. "Sign in with the account
              that owns this studio…") — previously defined on the error but
              never rendered, leaving the headline without its remedy. */}
          {error.detail ? <p className='max-w-md text-md text-muted'>{error.detail}</p> : null}
        </div>
        {recoveryError && <p role='alert'>{recoveryError}</p>}
        <div className='flex items-center gap-12'>
          {error.retryFn ? (
            <button
              disabled={pending}
              onClick={retry}
              className='rounded-8 bg-primary px-24 py-8 text-md font-medium text-white transition-colors hover:bg-primary-highlight'
              data-testid='boot-retry-button'
              {...STagNew(selector, 'retry')}
            >
              Try Again
            </button>
          ) : (
            <button
              disabled={pending}
              onClick={retry}
              className='rounded-8 bg-primary px-24 py-8 text-md font-medium text-white transition-colors hover:bg-primary-highlight'
              data-testid='boot-retry-button'
              {...STagNew(selector, 'retry')}
            >
              Reload
            </button>
          )}
          {/* Account escape for account-scoped errors (cross-account bridge
              link): retry cannot help an account that does not own the linked
              studio — sign-out is the actionable path the detail copy asks
              for. Rendered only when the error supplies it. */}
          {error.signOutFn ? (
            <button
              onClick={error.signOutFn}
              // Per rule: keyboard-reachable-interactive-elements (ai.knowledge/global) —
              // secondary buttons need an explicit focus-visible ring.
              className='focus-visible:ring-primary/50 rounded-8 border border-base-100 px-24 py-8 text-md font-medium text-white transition-colors hover:bg-base-100 focus-visible:ring-2'
              data-testid='boot-sign-out-button'
            >
              Sign out
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
